import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import type { BlockyTimeDB } from '@/db/schema'
import {
  erase as eraseEntries,
  upsert,
  type ChangeSet,
  type EntryContent,
} from '@/core/repository/entryRepository'

/**
 * The marking loop.
 *
 * Thin by design: it decides WHAT to write, entryRepository decides HOW. All
 * database access goes through the repository so the slot invariant has exactly
 * one guardian.
 */

export interface Selection {
  anchor: BlockSlot
  focus: BlockSlot
}

export function singleSelection(slot: BlockSlot): Selection {
  return { anchor: slot, focus: slot }
}

/** Slots covered by a selection, in chronological order. */
export function selectionSlots(grid: BlockGrid, selection: Selection): BlockSlot[] {
  return grid.slotRange(selection.anchor, selection.focus)
}

export function selectionSize(grid: BlockGrid, selection: Selection): number {
  return selectionSlots(grid, selection).length
}

/**
 * Whether a slot lies within a selection.
 *
 * Compares against the range bounds rather than expanding the range, so this
 * stays O(1) even for a selection spanning days -- it runs on every long press.
 */
export function selectionContains(
  grid: BlockGrid,
  selection: Selection,
  slot: BlockSlot,
): boolean {
  const forward = grid.compareSlots(selection.anchor, selection.focus) <= 0
  const lo = forward ? selection.anchor : selection.focus
  const hi = forward ? selection.focus : selection.anchor

  return grid.compareSlots(lo, slot) <= 0 && grid.compareSlots(slot, hi) <= 0
}

export interface MarkOptions {
  skipOccupied?: boolean
}

export async function markSelection(
  grid: BlockGrid,
  selection: Selection,
  content: EntryContent,
  options: MarkOptions = {},
  db?: BlockyTimeDB,
): Promise<ChangeSet> {
  const slots = selectionSlots(grid, selection)
  return upsert(grid, content, slots, options, db)
}

export async function eraseSelection(
  grid: BlockGrid,
  selection: Selection,
  db?: BlockyTimeDB,
): Promise<ChangeSet> {
  return eraseEntries(selectionSlots(grid, selection), db)
}

/**
 * Where the cursor lands after marking.
 *
 * Cursor mode is what makes a whole day fillable in seconds: mark, and the
 * selection steps forward so the next tap is another event button rather than
 * another block. Returns null at the end of the window.
 */
export function advanceCursor(grid: BlockGrid, selection: Selection): Selection | null {
  const slots = selectionSlots(grid, selection)
  const last = slots[slots.length - 1]
  if (!last) return null

  const next = grid.addSlots(last, 1)
  if (!grid.isValidSlot(next)) return null

  return singleSelection(next)
}

/**
 * Total minutes covered by a selection.
 *
 * Derived from slot count rather than wall-clock subtraction, so a selection
 * spanning a DST transition reports the time actually lived: an hour vanishes
 * in spring and reappears in autumn.
 */
export function selectionMinutes(grid: BlockGrid, selection: Selection): number {
  return selectionSize(grid, selection) * grid.precisionMinutes
}

/** "2 h 30 m", "45 m", "1 h". */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} m`
  if (m === 0) return `${h} h`
  return `${h} h ${m} m`
}
